/* eslint-disable no-duplicate-imports */
import React, { FC, useState } from 'react';
import { Box, Stack, Divider, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { IArticleData } from '../LibraryTab';
import { LibraryLeftBarLoader } from './LibraryLoader';
import { FilterIcon } from '../../../../../utils/Icons';
import MySkeleton from '../../../../../components/Skeleton';
import { formatNumberWithCommas } from '../../../components/common';
import { HIDDEN_ARTICLE_IDS } from '../hiddenArticles';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

interface articleId {
  articleId: string;
}
interface Props {
  onClick: (data: articleId) => void;
  allArticleData: IArticleData[];
  selectedArticleId: string;
  handleClickOpenFilter: any;
  allArticleLoading: boolean;
  activeItemRef: React.MutableRefObject<any>;
}

const LibrarySideBar: FC<Props> = ({
  onClick,
  handleClickOpenFilter,
  allArticleData,
  selectedArticleId,
  activeItemRef,
  allArticleLoading
}: Props) => {
  const formattedLength = formatNumberWithCommas(allArticleData.length);
  // Phone: a cover that is missing or fails to load shows a quiet tile instead of the
  // browser's broken-image glyph and alt text; the list opens each resource full screen,
  // so it keeps no selected row.
  const isMobileApp = useIsMobileApp();
  const [brokenCovers, setBrokenCovers] = useState<Set<string>>(() => new Set());
  const markCoverBroken = (articleId: string) =>
    setBrokenCovers((prev) => (prev.has(articleId) ? prev : new Set(prev).add(articleId)));

  return (
    <Box
      height="100%"
      sx={{
        background: '#000000',
        color: '#FFFFFF',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      <Box px={2} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" py={2}>
          {allArticleLoading ? <MySkeleton variant="text" height={20} width={70} /> : 'Resources'}
        </Typography>
        <Box display="flex" alignItems="center" gap="8px">
          <Typography>
            {allArticleLoading ? <MySkeleton variant="text" height={20} width={55} /> : `All (${formattedLength})`}
          </Typography>
          {/* <FilterIcon size={34} onClick={handleClickOpenFilter} /> */}
        </Box>
      </Box>
      <Box height="calc(100% - 74px)" sx={{ overflowY: 'auto' }}>
        {allArticleLoading ? (
          <LibraryLeftBarLoader />
        ) : allArticleData.length === 0 ? (
          <Stack direction="row" spacing={2} p={2}>
            <Typography textAlign="center" p={2}>
              No Resource Found
            </Typography>
          </Stack>
        ) : allArticleData ? (
          allArticleData.map((ele) => {
            // Already left out of allArticleData (LibraryTab); kept here for any other caller.
            if (!HIDDEN_ARTICLE_IDS.has(ele.articleId)) {
              // Phone: no selected row, and no ref for the delayed scroll-to-selected to snap the shared scroll area to.
              const isSelected = ele.articleId === selectedArticleId && !isMobileApp;
              const coverMissing = isMobileApp && (!ele?.coverImageUrl || brokenCovers.has(ele.articleId));
              return (
                <Box key={ele.articleId} ref={isSelected ? activeItemRef : null}>
                  <Stack
                    direction="row"
                    spacing={2}
                    p={2}
                    data-testid="article-list-item"
                    onClick={() => onClick(ele)}
                    sx={{
                      cursor: 'pointer',
                      background: isSelected ? '#0071A9' : 'transparent',
                      color: '#FFFFFF',
                      '&:hover': {
                        background: isSelected ? '#0071A9' : '#141414'
                      },
                      [MOBILE_SX]: {
                        p: 1.5,
                        alignItems: 'flex-start',
                        '&:hover': { background: 'transparent' },
                        '&:active': { background: '#141414' }
                      }
                    }}
                  >
                    <Box sx={{ [MOBILE_SX]: { flexShrink: 0 } }}>
                      {allArticleLoading ? (
                        <MySkeleton variant="rectangular" height={79} width={118} />
                      ) : coverMissing ? (
                        <Box
                          aria-hidden
                          sx={{
                            width: 96,
                            height: 64,
                            borderRadius: '8px',
                            bgcolor: '#1C1C1C',
                            border: '1px solid #2A2A2A',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#868686'
                          }}
                        >
                          <ArticleOutlinedIcon sx={{ fontSize: 24 }} />
                        </Box>
                      ) : isMobileApp ? (
                        <Box
                          component="img"
                          src={ele.coverImageUrl}
                          alt=""
                          onError={() => markCoverBroken(ele.articleId)}
                          sx={{ display: 'block', width: 96, height: 64, objectFit: 'cover', borderRadius: '8px' }}
                        />
                      ) : (
                        <img
                          src={ele?.coverImageUrl}
                          alt="resumeimg"
                          width="118"
                          height="79"
                          style={{
                            objectFit: 'cover',
                            borderRadius: '8px',
                            minHeight: '79px',
                            minWidth: '118px'
                          }}
                        />
                      )}
                    </Box>
                    <Box width="calc(100% - 138px)" sx={{ [MOBILE_SX]: { width: 'auto', flex: 1, minWidth: 0 } }}>
                      {/* Phone: the whole title shows (no two-line clamp); the row grows instead. */}
                      <Typography
                        variant="h5"
                        className={isMobileApp ? undefined : 'lineClamp-2'}
                        sx={{
                          color: isSelected ? '#FFFFFF' : '', // Conditional color,
                          ...(ele?.articleName && !ele?.articleName.includes(' ') ? { wordBreak: 'break-all' } : {}), // Conditional word break
                          [MOBILE_SX]: { overflowWrap: 'anywhere' }
                        }}
                      >
                        {allArticleLoading ? <MySkeleton height={24} width="80%" /> : ele?.articleName}
                      </Typography>
                      {ele?.createdAt && (
                        <Typography sx={{ color: isSelected ? ' #FFFFFF' : '' }}>
                          {allArticleLoading ? (
                            <MySkeleton height={16} width="40%" />
                          ) : (
                            new Date(ele.createdAt * 1000).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Divider sx={{ borderColor: '#2A2A2A' }} />
                </Box>
              );
            } else return null
          })
        ) : (
          ''
        )}
      </Box>
    </Box>
  );
};

export default LibrarySideBar;
